import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // const API_BASE_URL = 'http://localhost:8000';
  const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';

  useEffect(() => {
    const storedToken = localStorage.getItem('mfu_superadmin_token');
    const storedUser = localStorage.getItem('mfu_superadmin_user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (parseError) {
        console.error('❌ Error parsing stored user data:', parseError);
        localStorage.removeItem('mfu_superadmin_token');
        localStorage.removeItem('mfu_superadmin_user');
      }
    }

    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.status === 'success') {
        const { user: userData, token: tokenData } = response.data.data;

        setUser(userData);
        setToken(tokenData);

        localStorage.setItem('mfu_superadmin_token', tokenData);
        localStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (mainBackendError) {
      // fallback: ลอง login ผ่าน MFU OpenAPI
      try {
        const mfuResponse = await axios.post(
          'https://server.dietz.asia/openApi/mfu/v1.0.0/get_token_by_username_and_password.php',
          { username, password },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'DDQ8y7V19LaWvI*j'
            }
          }
        );

        if (mfuResponse.data.result && mfuResponse.data.consultant) {
          const consultantData = mfuResponse.data.consultant;
          const tokenData = mfuResponse.data.token;

          const userData = {
            id: consultantData.id,
            username: consultantData.username,
            name: consultantData.name,
            lastname: consultantData.lastname,
            nameprefix: consultantData.nameprefix,
            email: consultantData.email,
            tel: consultantData.tel,
            level: consultantData.level,
            role: consultantData.role || [],
            typeconsult: consultantData.typeconsult,
            typebranch: consultantData.typebranch,
            consultCampaign: consultantData.consultCampaign,
            fullName: [consultantData.nameprefix, consultantData.name, consultantData.lastname]
              .filter(p => p && p.trim())
              .join(' ') || consultantData.username
          };

          setUser(userData);
          setToken(tokenData);

          localStorage.setItem('mfu_superadmin_token', tokenData);
          localStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));

          return { success: true, user: userData };
        } else {
          const errorMsg = mfuResponse.data.responseText || 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง';
          console.error('❌ MFU OpenAPI login failed:', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (mfuError) {
        console.error('❌ MFU OpenAPI login error:', mfuError.message);
        return {
          success: false,
          message: mfuError.response?.data?.responseText || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
        };
      }
    }
  };

  const loginWithToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString
      });

      if (response.data.status === 'success' && response.data.data) {
        const userData = response.data.data.user;

        setUser(userData);
        setToken(tokenString);

        localStorage.setItem('mfu_superadmin_token', tokenString);
        localStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Invalid token' };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Token ไม่ถูกต้องหรือหมดอายุ'
      };
    }
  };

  const verifyToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString || token
      });

      if (response.data.status !== 'success') {
        throw new Error('Token is invalid');
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (user?.id) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {
          user_id: user.id
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);

      localStorage.removeItem('mfu_superadmin_token');
      localStorage.removeItem('mfu_superadmin_user');
    }
  };

  const getProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 'success') {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  const getUserFullName = () => {
    if (!user) return 'ไม่ระบุ';

    if (user.fullName) return user.fullName;

    const parts = [];
    if (user.nameprefix) parts.push(user.nameprefix);
    if (user.name) parts.push(user.name);
    if (user.lastname) parts.push(user.lastname);

    return parts.length > 0 ? parts.join(' ') : (user.username || 'ไม่ระบุ');
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    loginWithToken,
    logout,
    getProfile,
    getUserFullName
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
